// frontend/src/App.js

import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useParams, NavLink } from 'react-router-dom';
import { API_BASE_URL } from './config';
import { verify2FA } from './api';
import useRouteSync from './hooks/useRouteSync';
import Breadcrumbs from './components/Breadcrumbs';
import { pathForTab } from './routes';
import {
  CASE_TYPE_OPTIONS, CASE_STATUS_OPTIONS, CASE_PHASE_OPTIONS,
  CASE_TYPE_STAGES_MAP, getStagesForType as getStagesForTypeUtil, CASE_STAGE_OPTIONS,
} from './utils/caseOptions';


// Import modular components
import AuthForms from './components/AuthForms';
import SetPassword from './components/SetPassword';
import ClientManagement from './components/ClientManagement';
import CaseManagement from './components/CaseManagement';
import DocumentManagement from './components/DocumentManagement';
import ClauseLibrary from './components/ClauseLibrary';
import TimeExpenseManagement from './components/TimeExpenseManagement';
import BillingManagement from './components/BillingManagement';
import CommunicationLogManagement from './components/CommunicationLogManagement';
import CollectionsSettings from './components/CollectionsSettings';
import CollectionsWorklist from './components/CollectionsWorklist';
import DebtCollectionDashboard from './components/DebtCollectionDashboard';
import TaskManagement from './components/TaskManagement';
import ReportsDashboard from './components/ReportsDashboard';
import TaskTemplatesManager from './components/TaskTemplatesManager';
import ServiceProductManagement from './components/ServiceProductManagement';
import ClientPortalLayout from './components/ClientPortalLayout';
import AuditorPortalLayout from './components/AuditorPortalLayout';
import AuditorErrorBoundary from './components/AuditorErrorBoundary';
import QuickMemoWidget from './components/QuickMemoWidget';
import AuditorAccountManager from './components/AuditorAccountManager';
import TrustAccounting from './components/TrustAccounting';
import CalendarModule from './components/Calendar';
import AuditManagement from './components/AuditManagement';
import ComplianceHub from './components/ComplianceHub';
import AnnualCertificateManagement from './components/AnnualCertificateManagement';
import AttorneyRecords from './components/AttorneyRecords';
import Toast from './components/Toast';
import NotificationList from './components/NotificationList';
import ImportWizard from './components/ImportWizard';
import PendingApprovalsInbox from './components/PendingApprovalsInbox';
import DocumentEditorPage from './components/DocumentEditorPage';
import BillableHoursCard from './components/BillableHoursCard';
import Dashboard from './components/Dashboard';
import CaseDetailsPage from './components/CaseDetailsPage';
import ChartOfAccounts from './components/ChartOfAccounts';
import GeneralLedger from './components/GeneralLedger';
import SupplierManagement from './components/SupplierManagement';
import CreditorManagement from './components/CreditorManagement';
import PettyCashManagement from './components/PettyCashManagement';
import AccountingReview from './components/AccountingReview';
import FirmExpenseManagement from './components/FirmExpenseManagement';
import BankAccountSettings from './components/BankAccountSettings';
import BankReconciliation from './components/BankReconciliation';
import VATReturns from './components/VATReturns';
import BillOfCostsManagement from './components/BillOfCostsManagement';
import FirmTaxSettings from './components/FirmTaxSettings';
import ModuleAccessControls from './components/ModuleAccessControls';
import IntegrationsSettings from './components/IntegrationsSettings';
import TwoFactorSetup from './components/TwoFactorSetup';
import UnlinkedInbox from './components/UnlinkedInbox';
import ComposeEmail from './components/ComposeEmail';
import EmailRules from './components/EmailRules';
import ActivityLog from './components/ActivityLog';
import MyPerformance from './components/MyPerformance';
import TeamOverview from './components/TeamOverview';
import PerformanceReviews from './components/PerformanceReviews';
import DepartmentManagement from './components/DepartmentManagement';
import PayrollManagement from './components/PayrollManagement';
import TrialBalanceReport from './components/TrialBalanceReport';
import BalanceSheetReport from './components/BalanceSheetReport';
import IncomeStatementReport from './components/IncomeStatementReport';
import CashFlowReport from './components/CashFlowReport';
import AccountingPeriods from './components/AccountingPeriods';
import { useConfirm } from './components/ConfirmProvider';
import RoleGate from './components/RoleGate';
import { canAccessTab } from './utils/rbac';
import { normalizeApiNextUrl } from './utils/normalizeApiNextUrl';
import { TimerProvider, useTimer } from './contexts/TimerContext';
import ConveyancingDashboard from './components/ConveyancingDashboard';
import PropertyManagement from './components/PropertyManagement';
import BankInstructionInbox from './components/BankInstructionInbox';
import ConveyancingBatchManager from './components/ConveyancingBatchManager';
import ConveyancingReports from './components/ConveyancingReports';

// Create a context to hold permissions
const PermissionsContext = React.createContext();
export const usePermissions = () => React.useContext(PermissionsContext);

// --- Icon Components for Sidebar ---
const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path}></path>
  </svg>
);

const DashboardIcon = () => <Icon path="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />;
const LockSidebarIcon = () => <Icon path="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />;
const ClientsIcon = () => <Icon path="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />;
const CasesIcon = () => <Icon path="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
const DocumentsIcon = () => <Icon path="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />;
const TimeExpenseIcon = () => <Icon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01M12 6v-1h4v1m-4 0h-4v-1h4m0-4v1m0 10v1m0 0v1m0-1h4v1m-4-1h-4v1m0 0h.01M12 12h4v-1h-4m0 0h-4v1h4m-4-1v-1m0 0v-1m0 0h.01" />;
const ServicesIcon = () => <Icon path="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />;
const BillingIcon = () => <Icon path="M4 7h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V9a2 2 0 012-2zm0 0V5a2 2 0 012-2h10a2 2 0 012 2v2M16 13h2" />;
const DebtRecoveryIcon = () => <Icon path="M12 2l8 4v6c0 5-3.582 9.74-8 10-4.418-.26-8-5-8-10V6l8-4z" />;
const CollectionsIcon = () => <Icon path="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />;
const TrustIcon = () => <Icon path="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.002 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.002 0M18 7l3 9m-3-9l-6-2" />;
const CommsIcon = () => <Icon path="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />;
const TasksIcon = () => <Icon path="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />;
const CalendarIcon = () => <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />;
const ReportsIcon = () => <Icon path="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />;
const ComplianceIcon = () => <Icon path="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.002 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.002 0M18 7l3 9m-3-9l-6-2" />; // Reusing TrustIcon for simplicity
const AuditIcon = () => <Icon path="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />;
const ImportsIcon = () => (
  <Icon path="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 7.5L12 3m0 0l4.5 4.5M12 3v13.5" />
);
const AttorneyRecordsIcon = () => (
  <Icon path="M15 19.128a9.003 9.003 0 003.75-7.128 9 9 0 10-18 0 9.003 9.003 0 003.75 7.128M15 19.128a9.06 9.06 0 01-3 .372 9.06 9.06 0 01-3-.372M15 19.128a3.375 3.375 0 00-3-1.878 3.375 3.375 0 00-3 1.878M12 12a3 3 0 100-6 3 3 0 000 6z" />
);
const ChartOfAccountsIcon = () => <Icon path="M3 10h18M3 14h18M3 6h18M3 18h18" />;
const GeneralLedgerIcon = () => <Icon path="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />;
const SuppliersIcon = () => <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />;
const CreditorsIcon = () => <Icon path="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />;
const PettyCashIcon = () => <Icon path="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />;
const FirmExpensesIcon = () => <Icon path="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />;
const BankAccountsIcon = () => <Icon path="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />;
const BankReconIcon = () => <Icon path="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />;
const VATReturnsIcon = () => <Icon path="M9 7h6m0 10v-3m-3 3v-6m-3 6v-1M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />;
const ActivityLogIcon = () => <Icon path="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />;
const MyPerformanceIcon = () => <Icon path="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />;
const TeamOverviewIcon = () => <Icon path="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />;
const ReviewsIcon = () => <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />;
const DepartmentsIcon = () => <Icon path="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />;
const LogoutIcon = () => <Icon path="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />;
const MenuIcon = ({ isCollapsed }) => (
    <Icon path={isCollapsed ? "M4 6h16M4 12h16M4 18h16" : "M6 18L18 6M6 6l12 12"} />
);
const ChevronIcon = ({ isOpen }) => (
  <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);


function AppContent() {
  // --- NEW: State for sidebar collapse ---
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Mobile-specific sidebar state (off-canvas overlay)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const confirm = useConfirm();

  // ... (all existing state declarations remain the same)
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [serviceProducts, setServiceProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [communicationLogs, setCommunicationLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [associatesList, setAssociatesList] = useState([]);
  const [paralegalsList, setParalegalsList] = useState([]);
  const [payments, setPayments] = useState([]);
  const [trustAccounts, setTrustAccounts] = useState([]);
  const [trustTransactions, setTrustTransactions] = useState([]);
  const [complianceItems, setComplianceItems] = useState([]);
  const [firms, setFirms] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [debtRecoveryMandates, setDebtRecoveryMandates] = useState([]);
  
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [currentUser, setCurrentUser] = useState(null);
  const [isStaff, setIsStaff] = useState(false);
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  // Resolved firm-scoped Module Access Control map ({invoicing: bool, ...}).
  // Mirrored to localStorage like userRole so nav gating is correct on the
  // first paint after a hard reload, before auth/current-user/ resolves.
  const [moduleAccess, setModuleAccess] = useState(() => {
    try { return JSON.parse(localStorage.getItem('moduleAccess')) || null; }
    catch { return null; }
  });
  const [selectedFirm, setSelectedFirm] = useState(localStorage.getItem('selectedFirm'));
  const [authLoading, setAuthLoading] = useState(true);

  // --- Request lifecycle management ---
  // Used to cancel in-flight requests (notably paginated fetch chains) on logout.
  const abortControllerRef = useRef(new AbortController());
  const isLoggingOutRef = useRef(false);

  const cancelInFlightRequests = useCallback((reason = 'auth-state-change') => {
    try {
      abortControllerRef.current?.abort(reason);
    } catch (_) {
      // ignore
    }
    abortControllerRef.current = new AbortController();
  }, []);

  const [activeTab, setActiveTabRaw] = useState('dashboard');
  // Bridge: sync activeTab ↔ URL via React Router
  const { setActiveTab } = useRouteSync(activeTab, setActiveTabRaw);
  const [toasts, setToasts] = useState([]);
  const [showLoginForm] = useState(true);
  const [navigateTo, setNavigateTo] = useState(null);
  // Document Editor state: { id, title, content } or null
  const [editingDocument, setEditingDocument] = useState(null);
  // --- Sidebar navigation state ---
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const sidebarScrollPosRef = useRef(0);
  
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  // 2FA state
  const [pending2FA, setPending2FA] = useState(null);   // { partial_token: '...' } when awaiting TOTP
  const [totpCode, setTotpCode] = useState('');
  
  // ... (all existing useEffect, useCallback, useMemo hooks remain the same)
  const authAxios = useMemo(() => {
    const instance = axios.create({
      baseURL: API_BASE_URL,
    });
  
    instance.interceptors.request.use(
      config => {
        // Dev hardening: if an absolute https URL slips through for local API,
        // rewrite it to http://localhost to avoid SSL protocol errors against Django runserver.
        if (process.env.NODE_ENV !== 'production') {
          const url = typeof config.url === 'string' ? config.url : '';
          const baseURL = typeof config.baseURL === 'string' ? config.baseURL : '';

          if (url.startsWith('https://127.0.0.1:8000/')) {
            config.url = url.replace('https://127.0.0.1:8000/', 'http://localhost:8000/');
            config.baseURL = undefined;
          } else if (url.startsWith('https://localhost:8000/')) {
            config.url = url.replace('https://localhost:8000/', 'http://localhost:8000/');
            config.baseURL = undefined;
          } else if (baseURL.startsWith('https://127.0.0.1:8000/')) {
            config.baseURL = baseURL.replace('https://127.0.0.1:8000/', 'http://localhost:8000/');
          } else if (baseURL.startsWith('https://localhost:8000/')) {
            config.baseURL = baseURL.replace('https://localhost:8000/', 'http://localhost:8000/');
          }
        }

        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          config.headers.Authorization = `Token ${currentToken}`;
        }
        
        const isSuperuser = localStorage.getItem('is_superuser') === 'true';
        const currentSelectedFirm = localStorage.getItem('selectedFirm');

        if (isSuperuser && currentSelectedFirm) {
          config.headers['X-Firm-ID'] = currentSelectedFirm;
        }

        // Attach shared abort signal so we can cancel request chains on logout.
        if (!config.signal && abortControllerRef.current) {
          config.signal = abortControllerRef.current.signal;
        }
  
        return config;
      },
      error => Promise.reject(error)
    );
  
    return instance;
  }, []); 

  // Fetch notifications for the current user
  const fetchNotifications = useCallback(async () => {
    try {
      // Support both unpaginated arrays and paginated DRF responses.
      // Some deployments paginate notifications (often defaulting to 10/16/20), which can look like a hard cap.
      const first = await authAxios.get('notifications/');
      const firstData = first?.data;

      if (Array.isArray(firstData)) {
        setNotifications(firstData);
        return;
      }

      if (firstData && Array.isArray(firstData.results)) {
        // Fetch subsequent pages (bounded) so the UI sees the full list.
        const maxToLoad = 500;
        let all = [...firstData.results];
        let nextUrl = firstData.next ? normalizeApiNextUrl(firstData.next) : null;
        let guard = 0;

        while (nextUrl && all.length < maxToLoad && guard < 50) {
          guard += 1;
          const resp = await authAxios.get(nextUrl);
          const data = resp?.data;
          if (!data || !Array.isArray(data.results)) break;
          all = all.concat(data.results);
          nextUrl = data.next ? normalizeApiNextUrl(data.next) : null;
        }

        setNotifications(all.slice(0, maxToLoad));
        return;
      }

      setNotifications([]);
    } catch (err) {
      // Board Members are not on NotificationViewSet's allow-list, so this
      // 403s for them — and it polls every 60 seconds, which filled the
      // console with a fresh pair of errors every minute. Same reasoning as
      // createFetchFunction: a role that cannot read notifications has none,
      // which is what the bell already renders. [login-errors]
      if (err?.response?.status !== 403) {
        console.error('Failed to fetch notifications', err);
      }
      setNotifications([]);
    }
  }, [authAxios]);

  // Poll notifications periodically (every 60s) – skip for auditors
  useEffect(() => {
    if (!token) return;
    const role = localStorage.getItem('userRole');
    if (role === 'AUDITOR') return;
    fetchNotifications();
    const id = setInterval(fetchNotifications, 60000);
    return () => clearInterval(id);
  }, [token, fetchNotifications]);
  
  const permissions = useMemo(() => {
    if (!userRole) return {};

    const isPartner = userRole === 'PARTNER';
    const isAssociate = userRole === 'ASSOCIATE';
    const isParalegal = userRole === 'PARALEGAL';
    const isSupportStaff = userRole === 'SUPPORT_STAFF';
    const isAccountant = userRole === 'ACCOUNTANT';
    const isBoardMember = userRole === 'BOARD_MEMBER';
    const isSuperuser = userRole === 'SUPERUSER';

    return {
        isPartner,
        isAssociate,
        isParalegal,
        isSupportStaff,
        isAccountant,
        isBoardMember,
        isSuperuser, // Add isSuperuser here
        canViewCases: isPartner || isAssociate || isParalegal || isSuperuser,
        canCreateCase: isPartner || isAssociate || isParalegal || isSuperuser,
        canEditCase: isPartner || isAssociate || isParalegal || isSuperuser,
        canDeleteCase: isPartner || isAssociate || isSuperuser,
        canPermanentlyDeleteCase: isPartner || isSuperuser,
        canViewDocuments: isPartner || isAssociate || isParalegal || isSuperuser,
        canCreateDocument: isPartner || isAssociate || isParalegal || isSuperuser,
        canEditDocument: isPartner || isAssociate || isParalegal || isSuperuser,
        canDeleteDocument: isPartner || isAssociate || isParalegal || isSuperuser,
        canVerifyDocument: isPartner || isAssociate || isSuperuser,
        canCreateTemplate: isPartner || isAssociate || isSuperuser,
        canEditTemplate: isPartner || isAssociate || isSuperuser,
        canLogTime: isPartner || isAssociate || isParalegal || isSuperuser,
        canLogExpense: isPartner || isAssociate || isParalegal || isSuperuser,
        // Billing / finance — broaden to include ACCOUNTANT (and ASSOCIATE
        // for matter-level invoice creation) so partners are not the sole
        // bottleneck for revenue capture and recovery.
        canViewBilling: isPartner || isAssociate || isAccountant || isParalegal || isSuperuser,
        canCreateInvoice: isPartner || isAssociate || isAccountant || isSuperuser,
        canEditInvoice: isPartner || isAssociate || isAccountant || isSuperuser,
        canDeleteInvoice: isPartner || isAccountant || isSuperuser,
        // Create-only billing access for paralegals: a new Draft invoice
        // (manual "New Invoice" form or "Generate from WIP"), or a new
        // ServiceProduct catalog entry. Deliberately separate from
        // `canCreateInvoice`, which also gates PreBillReview.js's pre-bill
        // *confirm* action and BatchBilling.js's bulk WIP generation —
        // neither of which paralegals should be able to reach.
        canContributeBilling: isPartner || isAssociate || isAccountant || isParalegal || isSuperuser,
        canManageTrustAccounts: isPartner || isAccountant || isSuperuser,
        canViewLogs: isPartner || isAssociate || isParalegal || isSupportStaff || isSuperuser,
        canCreateLog: isPartner || isAssociate || isParalegal || isSupportStaff || isSuperuser,
        canEditLog: isPartner || isAssociate || isParalegal || isSupportStaff || isSuperuser,
        canDeleteLog: isPartner || isAssociate || isSuperuser,
        canViewTasks: isPartner || isAssociate || isParalegal || isSuperuser,
        canCreateTask: isPartner || isAssociate || isParalegal || isSuperuser,
        canEditTask: isPartner || isAssociate || isParalegal || isSuperuser,
        canDeleteTask: isPartner || isAssociate || isSuperuser,
        canViewFinancialReports: isPartner || isAccountant || isBoardMember || isSuperuser,
        canViewInsightsReports: isPartner || isAccountant || isBoardMember || isSuperuser,
        canViewPersonalPerformance: isPartner || isAssociate || isSuperuser,
        canManageCompliance: isPartner || isSuperuser,
        canViewCompliance: isPartner || isAssociate || isParalegal || isSuperuser,
        // ACCOUNTANT can read the full staff directory for payroll admin work.
        canManageStaff: isPartner || isAccountant || isSuperuser,
        // PARTNER, ACCOUNTANT, or SUPPORT_STAFF (collections handlers) can manage dunning.
        canManageCollections: isPartner || isAccountant || isSupportStaff || isSuperuser,
        canViewAuditLog: isPartner || isSuperuser,
        canCreateClient: isPartner || isAssociate || isParalegal || isSupportStaff || isSuperuser,
        canEditClient: isPartner || isAssociate || isParalegal || isSuperuser,
        canDeleteClient: isPartner || isAssociate || isSuperuser,
        canApproveDocument: isPartner || isSuperuser,
        // Bulk imports (data onboarding). Accountants are restricted to the
        // financial entities (invoices/payments/expenses) by an in-method
        // allow-list in ImportViewSet.import_csv; this flag, TAB_ACCESS.imports
        // and the backend permission class must always be changed together —
        // they are not derived from one another and have silently drifted
        // apart before. [H11]
        canRunImports: isPartner || isSupportStaff || isParalegal || isAccountant || isSuperuser,
        // Dual-approval workflow: who can approve/reject a paralegal's
        // pending case or billing entry — matches IsApprovalOperator on
        // the backend (legal_ops/permissions.py).
        canApproveWork: isPartner || isAssociate || isSuperuser,
        // Resolved firm-scoped Module Access Control map ({invoicing: bool,
        // ...}) or null until auth/current-user/ resolves. Consumed by
        // RoleGate and the sidebar filter via canAccessTab's 4th arg so the
        // three surfaces (nav, content gate, API) can't drift.
        moduleAccess,
    };
}, [userRole, moduleAccess]);

  // Memoized options for dropdowns
  const genderOptions = useMemo(() => [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer_Not_To_Say', label: 'Prefer Not to Say' },
  ], []);

  // Case type/status/phase/stage option lists live in utils/caseOptions.js
  // (shared with CaseEditModal.js) - referenced directly rather than
  // rewrapped in useMemo since they're already stable module-level constants.
  const caseTypeOptions = CASE_TYPE_OPTIONS;
  const caseStatusOptions = CASE_STATUS_OPTIONS;
  const casePhaseOptions = CASE_PHASE_OPTIONS;
  const caseTypeStagesMap = CASE_TYPE_STAGES_MAP;
  const getStagesForType = getStagesForTypeUtil;
  const caseStageOptions = CASE_STAGE_OPTIONS;

  const riskLevelOptions = useMemo(() => [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
], []);

  const expenseCategoryOptions = useMemo(() => [
    { value: 'Filing Fees', label: 'Filing Fees' },
    { value: 'Travel', label: 'Travel' },
    { value: 'Lodging', label: 'Lodging' },
    { value: 'Research', label: 'Research' },
    { value: 'Printing', label: 'Printing' },
    { value: 'Consultant', label: 'Consultant Fees' },
    { value: 'Meals', label: 'Meals' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Serving', label: 'Serving' },
    { value: 'Other', label: 'Other' },
  ], []);

  const invoiceStatusOptions = useMemo(() => [
    { value: 'Pre_Bill', label: 'Pre-Bill' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Sent', label: 'Sent' },
    { value: 'Partial', label: 'Partial' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Overdue', label: 'Overdue' },
    { value: 'Void', label: 'Void' },
    { value: 'Cancelled',label: 'Cancelled'},
  ], []);

  const documentTypeOptions = useMemo(() => [
    { value: 'Client', label: 'Client Management'},
    { value: 'Pleadings', label: 'Pleadings' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Correspondence', label: 'Correspondence' },
    { value: 'Evidence', label: 'Evidence' },
    { value: 'Research', label: 'Research' },
    { value: 'Internal_Memo/Draft', label: 'Internal Memo/Draft' },
    { value: 'Agreement', label: 'Agreement' },
    { value: 'OTHER', label: 'Other' },
  ], []);

  const communicationLogTypeOptions = useMemo(() => [
    { value: 'Email', label: 'Email' },
    { value: 'SMS', label: 'SMS' },
    { value: 'Letter', label: 'Letter' },
    { value: 'WhatsApp', label: 'WhatsApp' },
    { value: 'Call', label: 'Call' },
    { value: 'Meeting', label: 'Meeting' },
    { value: 'Courier', label: 'Courier' },
    { value: 'InPerson', label: 'In Person' },
    { value: 'Message', label: 'Message' },
    { value: 'Note', label: 'Note' },
    { value: 'Other', label: 'Other' },
  ], []);

  const taskStatusOptions = useMemo(() => [
    { value: 'To-Do', label: 'To-Do'},
    { value: 'Pending', label: 'Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Deferred', label: 'Deferred' },
    { value: 'Blocked', label: 'Blocked' },
  ], []);

  const taskPriorityOptions = useMemo(() => [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Urgent', label: 'Urgent' },
  ], []);

  const documentTypeChoices = useMemo(() => [
    { value: 'View', label: 'View' },
    { value: 'Doanload', label: 'Download' },
    { value: 'Edit', label: 'Edit' },
    { value: 'Delete', label: 'Delete' },
  ], []);


  const showFormMessage = useCallback((type, text, opts = {}) => {
    if (!text) return; // some callers push ('', '') purely to clear prior state; nothing to show
    const duration = typeof opts.duration === 'number' ? opts.duration : 10000;
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2,8);
    const toast = { id, type, text };
    setToasts(prev => [...prev, toast]);
    // auto-remove
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Handle OAuth redirect: /?outlook_connected=1 lands on root because Azure
  // Storage only serves index.html for "/". Detect the param, switch to the
  // integrations tab and show a toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('outlook_connected') === '1') {
      setActiveTab('integrations');
      showFormMessage('success', 'Outlook account connected successfully!');
      // Clean up URL
      params.delete('outlook_connected');
      const clean = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
    } else if (params.get('outlook_error')) {
      setActiveTab('integrations');
      showFormMessage('error', `Outlook connection failed: ${params.get('outlook_error')}`);
      params.delete('outlook_error');
      const clean = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
    }
  }, [setActiveTab, showFormMessage]);

  // --- MODIFICATION: Updated createFetchFunction to handle pagination ---
  const createFetchFunction = useCallback((endpoint, setState, resourceName) => {
    // This function will fetch all pages for a paginated resource
    const fetchAllPages = async (url, accumulatedData = []) => {
      try {
        // If the user is logged out mid-chain, stop without firing more requests.
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
          return accumulatedData;
        }

        const response = await authAxios.get(url);
        const data = response.data;

        let results = [];
        if (data && Array.isArray(data.results)) {
          results = accumulatedData.concat(data.results);
        } else if (Array.isArray(data)) {
          // Handle non-paginated array responses
          return data;
        } else {
          // Handle unexpected data structure
          console.error(`API response for ${resourceName} at ${url} is not an array or paginated object:`, data);
          return accumulatedData;
        }

        // Check for next page
        if (data.next) {
          // Recursively fetch the next page
          return await fetchAllPages(normalizeApiNextUrl(data.next), results);
        } else {
          // Last page, return all accumulated results
          return results;
        }
      } catch (error) {
        // Ignore abort/cancel noise (commonly triggered by logout).
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
          return accumulatedData;
        }
        // If token is gone or backend denies access, stop the chain.
        if (!localStorage.getItem('token') || error?.response?.status === 401) {
          return accumulatedData;
        }

        // A 403 is an expected answer, not a failure.
        //
        // fetchAllData() requests the same resource set for every role, and
        // the backend is the source of truth for what each role may see — so
        // a Board Member, who is restricted to four report views, legitimately
        // gets 403 for trust accounts, trust transactions, compliance items
        // and several reports on every single login. Logging those as errors
        // filled the console with red on a perfectly normal sign-in and made
        // real failures harder to spot.
        //
        // Handled here rather than by gating the fetch list per role in
        // JavaScript: that would add a fourth place where a role-to-resource
        // mapping lives, and this codebase has repeatedly been bitten by those
        // copies drifting apart. The role that cannot see a resource simply
        // gets an empty list, which is what the UI already renders for it.
        if (error?.response?.status === 403) {
          return accumulatedData;
        }

        console.error(`Error fetching ${resourceName} from ${url}:`, error);
        // Return what we have so far
        return accumulatedData;
      }
    };

    // Return the function that starts the fetching process
    return async () => {
      const allData = await fetchAllPages(endpoint);
      setState(allData);
    };
  }, [authAxios]);
  // --- END MODIFICATION ---
  
  const fetchClients = useCallback(createFetchFunction('clients/', setClients, 'clients'), [createFetchFunction]);
  const fetchCases = useCallback(createFetchFunction('cases/', setCases, 'cases'), [createFetchFunction]);
  const fetchDocuments = useCallback(createFetchFunction('documents/', setDocuments, 'documents'), [createFetchFunction]);
  const fetchTimeEntries = useCallback(createFetchFunction('time-entries/', setTimeEntries, 'time entries'), [createFetchFunction]);
  const fetchExpenseEntries = useCallback(createFetchFunction('expense-entries/', setExpenseEntries, 'expense entries'), [createFetchFunction]);
  const fetchServiceProducts = useCallback(createFetchFunction('service-products/', setServiceProducts, 'service products'), [createFetchFunction]);
  const fetchInvoices = useCallback(createFetchFunction('invoices/', setInvoices, 'invoices'), [createFetchFunction]);
  const fetchInvoiceItems = useCallback(createFetchFunction('invoice-items/', setInvoiceItems, 'invoice items'), [createFetchFunction]);
  const fetchCommunicationLogs = useCallback(createFetchFunction('communication-logs/', setCommunicationLogs, 'communication logs'), [createFetchFunction]);
  const fetchTasks = useCallback(createFetchFunction('tasks/', setTasks, 'tasks'), [createFetchFunction]);
  const fetchUsers = useCallback(createFetchFunction('users/', setUsers, 'users'), [createFetchFunction]);
  const fetchPartners = useCallback(createFetchFunction('users/?role=PARTNER', setPartners, 'partners'), [createFetchFunction]);
  const fetchAssociatesList = useCallback(createFetchFunction('users/?role=ASSOCIATE', setAssociatesList, 'associates'), [createFetchFunction]);
  const fetchParalegalsList = useCallback(createFetchFunction('users/?role=PARALEGAL', setParalegalsList, 'paralegals'), [createFetchFunction]);
  const fetchDocumentVersions = useCallback(createFetchFunction('document-versions/', setDocumentVersions, 'document versions'), [createFetchFunction]);
  const fetchDocumentTemplates = useCallback(createFetchFunction('document-templates/', setDocumentTemplates, 'document templates'), [createFetchFunction]);
  const fetchPayments = useCallback(createFetchFunction('payments/', setPayments, 'payments'), [createFetchFunction]);
  const fetchTrustAccounts = useCallback(createFetchFunction('trust-accounts/', setTrustAccounts, 'trust accounts'), [createFetchFunction]);
  const fetchTrustTransactions = useCallback(createFetchFunction('trust-transactions/', setTrustTransactions, 'trust transactions'), [createFetchFunction]);
  const fetchComplianceItems = useCallback(createFetchFunction('compliance-items/', setComplianceItems, 'compliance items'), [createFetchFunction]);

  const fetchDebtRecoveryMandates = useCallback(async () => {
    try {
      const resp = await authAxios.get('debt-recovery-requests/my-mandates/');
      setDebtRecoveryMandates(resp.data || []);
    } catch (error) {
      // Client portal endpoint: non-client users will 403 and that's fine.
      setDebtRecoveryMandates([]);
    }
  }, [authAxios]);

  const [debtMetrics, setDebtMetrics] = useState(null);

  const fetchDebtMetrics = useCallback(async () => {
    try {
      const resp = await authAxios.get('invoices/debt_metrics/');
      setDebtMetrics(resp.data);
    } catch (error) {
      // Partner-only endpoint: non-partner roles will 403 and that's fine.
      setDebtMetrics(null);
    }
  }, [authAxios]);

  const fetchRecentActivity = useCallback(async () => {
    try {
      const response = await authAxios.get('dashboard/activity/');
      setRecentActivity(response.data);
    } catch (error) {
      // Same reasoning as createFetchFunction: a role without access to the
      // activity feed gets 403, which is an answer rather than a fault.
      if (error?.response?.status !== 403) {
        console.error("Error fetching recent activity:", error);
      }
      setRecentActivity([]);
    }
  }, [authAxios]);

  const fetchAllData = useCallback(async () => {
    await Promise.all([
        fetchClients(),
        fetchCases(),
        fetchDocuments(),
        fetchTimeEntries(),
        fetchExpenseEntries(),
        fetchServiceProducts(),
        fetchInvoices(),
        fetchInvoiceItems(),
        fetchCommunicationLogs(),
        fetchTasks(),
  fetchUsers(),
  fetchPartners(),
  fetchAssociatesList(),
  fetchParalegalsList(),
        fetchDocumentVersions(),
        fetchDocumentTemplates(),
        fetchPayments(),
        fetchTrustAccounts(),
        fetchTrustTransactions(),
        fetchComplianceItems(),
        fetchRecentActivity(),
        fetchDebtRecoveryMandates(),
    ]);
  }, [
      fetchClients, fetchCases, fetchDocuments, fetchTimeEntries, fetchExpenseEntries, 
      fetchServiceProducts, fetchInvoices, fetchInvoiceItems, fetchCommunicationLogs, 
      fetchTasks, fetchUsers, fetchDocumentVersions, fetchDocumentTemplates, fetchPayments,
      fetchTrustAccounts, fetchTrustTransactions, fetchComplianceItems, fetchRecentActivity,
      fetchDebtRecoveryMandates
  ]);

  const [financialReports, setFinancialReports] = useState({});
  const [caseloadReports, setCaseloadReports] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [insightsReports, setInsightsReports] = useState({});
  const [reportDateRange, setReportDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
    };
  });

  const caseRouteMatch = window.location.pathname.match(/^\/cases\/([^/]+)(?:\/([^/]+))?$/);
  const isCaseDetailsRoute = !!caseRouteMatch;
  const caseRouteId = caseRouteMatch ? caseRouteMatch[1] : null;
  const caseRouteSubtab = caseRouteMatch ? caseRouteMatch[2] : undefined;
  const onCaseDetailsBack = useCallback(() => {
    setActiveTab('cases');
  }, [setActiveTab]);


  const createReportFetchFunction = useCallback((endpoint, setter) => {
      return async (startDate, endDate) => {
          try {
              const params = {
                  start_date: startDate,
                  end_date: endDate,
              };
              const response = await authAxios.get(endpoint, { params });
              setter(response.data);
          } catch (error) {
              // A role that may not read this report gets 403 — expected, not
              // a fault. Board Members reach only four report views, so the
              // other three logged an error on every login.
              if (error?.response?.status !== 403) {
                  console.error(`Error fetching report from ${endpoint}:`, error);
              }
          }
      };
  }, [authAxios]);

  const fetchFinancialReports = useCallback(createReportFetchFunction('reports/financial/', setFinancialReports), [createReportFetchFunction]);
  const fetchCaseloadReports = useCallback(createReportFetchFunction('reports/caseload/', setCaseloadReports), [createReportFetchFunction]);
  const fetchPerformanceMetrics = useCallback(createReportFetchFunction('reports/performance/', setPerformanceMetrics), [createReportFetchFunction]);
  const fetchInsightsReports = useCallback(createReportFetchFunction('reports/insights/', setInsightsReports), [createReportFetchFunction]);


  const handleDelete = useCallback(async (endpoint, id, itemType, fetchFunction) => {
    const ok = await confirm({
      title: 'Confirm deletion',
      message: `Are you sure you want to delete this ${itemType}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await authAxios.delete(`${endpoint}/${id}/`);
      showFormMessage('success', `${itemType} deleted successfully!`);
      if (fetchFunction) fetchFunction();
    } catch (error) {
      console.error(`Error deleting ${itemType}:`, error.response ? error.response.data : error.message);
      showFormMessage('danger', `Failed to delete ${itemType}.`);
    }
  }, [authAxios, showFormMessage, confirm]);

  // Store the resolved Module Access Control map (from login / 2FA verify /
  // auth/current-user/). Mirrored to localStorage so nav gating is right on
  // the first paint after a hard reload.
  const applyModuleAccess = useCallback((map) => {
    const value = map && typeof map === 'object' ? map : null;
    setModuleAccess(value);
    try {
      if (value) localStorage.setItem('moduleAccess', JSON.stringify(value));
      else localStorage.removeItem('moduleAccess');
    } catch { /* private mode / quota — in-memory state still works */ }
  }, []);

  const handleLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    // Cancel any in-flight paginated fetches (prevents page=2+ unauthorized spam).
    cancelInFlightRequests('logout');

    try {
      await authAxios.post('auth/logout/');
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('selectedFirm');
      localStorage.removeItem('is_superuser');
      localStorage.removeItem('userRole');
      localStorage.removeItem('moduleAccess');
      setToken(null);
      setCurrentUser(null);
      setIsStaff(false);
      setUserRole(null);
      setModuleAccess(null);
      setSelectedFirm(null);
      setFirms([]);
      showFormMessage('success', 'Logged out successfully!');
      setActiveTab('dashboard');
      isLoggingOutRef.current = false;
    }
  }, [authAxios, showFormMessage, cancelInFlightRequests]);

  const handleAuthInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleLoginSubmit = useCallback(async (e) => {
    e.preventDefault();
    showFormMessage('', '');
    try {
      const response = await axios.post(`${API_BASE_URL}auth/login/`, loginData);

      // --- 2FA gate ---
      if (response.data.requires_2fa) {
        setPending2FA({ partial_token: response.data.partial_token });
        setTotpCode('');
        showFormMessage('info', 'Enter the 6-digit code from your authenticator app.');
        return;
      }

      const { token: receivedToken, role, ...userData } = response.data;
      localStorage.setItem('token', receivedToken);
      localStorage.setItem('is_superuser', userData.is_superuser);
      localStorage.setItem('userRole', role);
      setToken(receivedToken);
      setCurrentUser(userData);
      setIsStaff(userData.is_staff);
      setUserRole(role);
      applyModuleAccess(userData.module_access);
      cancelInFlightRequests('login');
      showFormMessage('success', 'Logged in successfully!');
      setActiveTab('dashboard');
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.response?.data?.non_field_errors?.[0] || 'Login failed. Please check your credentials.';
      showFormMessage('danger', errorMessage);
    }
  }, [loginData, showFormMessage, cancelInFlightRequests, applyModuleAccess]);

  const handle2FASubmit = useCallback(async (e) => {
    e.preventDefault();
    showFormMessage('', '');
    if (!pending2FA?.partial_token || !totpCode) return;
    try {
      const data = await verify2FA(pending2FA.partial_token, totpCode);
      const { token: receivedToken, role, ...userData } = data;
      localStorage.setItem('token', receivedToken);
      localStorage.setItem('is_superuser', userData.is_superuser);
      localStorage.setItem('userRole', role);
      setToken(receivedToken);
      setCurrentUser(userData);
      setIsStaff(userData.is_staff);
      setUserRole(role);
      applyModuleAccess(userData.module_access);
      setPending2FA(null);
      setTotpCode('');
      cancelInFlightRequests('login');
      showFormMessage('success', 'Logged in successfully!');
      setActiveTab('dashboard');
    } catch (error) {
      console.error("2FA verify error:", error);
      const msg = error.response?.data?.detail || 'Invalid code. Please try again.';
      showFormMessage('danger', msg);
      // If token expired, reset to login
      if (error.response?.status === 401) {
        setPending2FA(null);
        setTotpCode('');
      }
    }
  }, [pending2FA, totpCode, showFormMessage, cancelInFlightRequests, applyModuleAccess]);

  const navigateToItem = (resource) => {
      if (!resource || !resource.type) return;

      const { type, id } = resource;
      let tabName = '';

      switch (type) {
          case 'case_open': case 'case_close': tabName = 'cases'; break;
          case 'time_entry': case 'expense_entry': tabName = 'timeExpense'; break;
          case 'invoice_due': tabName = 'billing'; break;
          case 'communication_log': tabName = 'communicationLogs'; break;
          case 'task_due': tabName = 'tasks'; break;
          case 'document': tabName = 'documents'; break;
          case 'attorney_records': tabName = 'attorneyRecords'; break;
          default: return;
      }
      setActiveTab(tabName);
    setNavigateTo({ tab: tabName, id: id, type: type, autoOpen: !!resource.autoOpen });
  };

  // Cross-module navigation helpers
  const navigateToTrustLedger = useCallback((trustAccountId, opts = {}) => {
    if (!trustAccountId) return;
    setActiveTab('trustAccounting');
    setNavigateTo({ tab: 'trustAccounting', type: 'trust_account_ledger', id: trustAccountId, ...opts });
  }, []);

  const navigateToAuditSupportTrustSummary = useCallback((opts = {}) => {
    setActiveTab('auditManagement');
    setNavigateTo({ tab: 'auditManagement', type: 'audit_subtab', subTab: 'annualSupport', reportKey: 'trust_summary', ...opts });
  }, []);
  
  const handleActivityClick = (activity) => {
    if (!activity) return;

    switch (activity.type) {
      case 'task': navigateToItem({ type: 'task_due', id: activity.id }); break;
      case 'invoice': navigateToItem({ type: 'invoice_due', id: activity.id }); break;
      case 'case': navigateToItem({ type: 'case_close', id: activity.id }); break;
      case 'communication_log': navigateToItem({ type: 'communication_log', id: activity.id }); break;
      case 'event': setActiveTab('calendar'); break;
      case 'compliance': setActiveTab('complianceHub'); setNavigateTo({ tab: 'complianceHub', id: activity.id, type: 'compliance' }); break;
      default: console.warn('Unknown activity type:', activity.type);
    }
  };

  const handleFirmSwitch = (firmId) => {
    const firmIdStr = firmId ? String(firmId) : '';
    setSelectedFirm(firmIdStr);
    if(firmIdStr) {
      localStorage.setItem('selectedFirm', firmIdStr);
    } else {
      localStorage.removeItem('selectedFirm');
    }
  };

  useEffect(() => {
    const verifyTokenAndGetUser = async () => {
        if (token) {
            try {
                const userResponse = await authAxios.get('auth/current-user/');
                setCurrentUser(userResponse.data);
                setIsStaff(userResponse.data.is_staff);
                applyModuleAccess(userResponse.data.module_access);
            } catch (error) {
                // Only force logout on actual auth failures.
                // Network/HTTPS/mixed-content glitches or aborted requests should not boot the user.
                const status = error?.response?.status;
                if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                  console.warn('Token verification canceled; keeping session.');
                } else if (status === 401 || status === 403) {
                  console.error("Token verification failed:", error);
                  handleLogout();
                } else {
                  console.error('Token verification request failed (non-auth):', error);
                }
            }
        }
        setAuthLoading(false);
    };
    verifyTokenAndGetUser();
  }, [token, authAxios, handleLogout, applyModuleAccess]);

  // Auto-open detail modal cross-tab (dispatch custom event)
  useEffect(() => {
    if (navigateTo && navigateTo.autoOpen && activeTab === navigateTo.tab) {
      // Emit a custom event so the active management component can open its detail modal
      window.dispatchEvent(new CustomEvent('open-resource-detail', { detail: navigateTo }));
    }
  }, [navigateTo, activeTab]);

  useEffect(() => {
    if (!authLoading && currentUser) {
        // Auditor portal fetches its own data independently – skip the
        // staff data pipeline to avoid 20+ unnecessary API calls.
        if (userRole === 'AUDITOR') return;

        fetchAllData();
        const { startDate, endDate } = reportDateRange;
        fetchFinancialReports(startDate, endDate);
        fetchCaseloadReports(startDate, endDate);
        fetchPerformanceMetrics(startDate, endDate);
        fetchInsightsReports(startDate, endDate);
        if (currentUser.is_superuser) {
            const fetchFirms = createFetchFunction('firms/', setFirms, 'firms');
            fetchFirms();
        }

        // Partner dashboard enhancement: collections efficiency summary.
        if (currentUser.is_superuser || userRole === 'PARTNER') {
          fetchDebtMetrics();
        }
    }
  }, [
      authLoading, currentUser, selectedFirm, fetchAllData, createFetchFunction,
      reportDateRange, fetchFinancialReports, fetchCaseloadReports, 
      fetchPerformanceMetrics, fetchInsightsReports, userRole, fetchDebtMetrics
  ]);

  const individualClients = useMemo(() => clients.filter(client => client.client_type === 'INDIVIDUAL'), [clients]);
  const organizationalClients = useMemo(() => clients.filter(client => client.client_type === 'ORGANIZATIONAL'), [clients]);

  const DashboardStatCard = ({ title, value, icon, iconBgColor, trend }) => (
    <div className="stat-card">
      <div className={`stat-card-icon ${iconBgColor}`}>
        {icon}
      </div>
      <div className="flex-1">
        <p className="stat-card-title">{title}</p>
        <p className="stat-card-value">{value}</p>
      </div>
      {trend && <span className="stat-card-trend">{trend}</span>}
    </div>
  );

  // --- Sidebar navigation helpers ---
  const toggleGroup = useCallback((groupKey) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  // Preserve sidebar nav scroll position across re-renders (the Sidebar
  // function is defined inside App, so React remounts it on every render —
  // this effect restores the scroll position synchronously after each paint).
  useLayoutEffect(() => {
    const el = document.getElementById('sidebar-nav-container');
    if (!el) return;
    el.scrollTop = sidebarScrollPosRef.current;
    const onScroll = () => { sidebarScrollPosRef.current = el.scrollTop; };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  });

  // --- Sidebar Component ---
  const Sidebar = ({ userRole }) => {
    const navGroups = useMemo(() => [
      {
        key: 'main',
        label: null,
        items: [
          { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
        ]
      },
      {
        key: 'practice',
        label: 'Practice Management',
        items: [
          { id: 'clients', label: 'Clients', icon: <ClientsIcon /> },
          { id: 'cases', label: 'Cases', icon: <CasesIcon /> },
          { id: 'pendingApprovals', label: 'Pending My Approval', icon: <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> },
          { id: 'documents', label: 'Documents', icon: <DocumentsIcon /> },
          { id: 'clauseLibrary', label: 'Clause Library', icon: <DocumentsIcon /> },
          { id: 'tasks', label: 'Tasks', icon: <TasksIcon /> },
          { id: 'taskTemplates', label: 'Task Templates', icon: <TasksIcon /> },
          { id: 'calendar', label: 'Calendar', icon: <CalendarIcon /> },
          { id: 'unlinkedInbox', label: 'Outlook Inbox', icon: <CommsIcon /> },
          { id: 'emailRules', label: 'Email Rules', icon: <CommsIcon /> },
          { id: 'communicationLogs', label: 'Logs', icon: <CommsIcon /> },
        ]
      },
      {
        key: 'billing',
        label: 'Time & Billing',
        items: [
          { id: 'timeExpense', label: 'Time & Disbursements', icon: <TimeExpenseIcon /> },
          { id: 'serviceProducts', label: 'Services', icon: <ServicesIcon /> },
          { id: 'billing', label: 'Billing', icon: <BillingIcon /> },
          { id: 'debtCollectionDashboard', label: 'Debt Recovery', icon: <DebtRecoveryIcon /> },
          { id: 'collectionsWorklist', label: 'Collections Worklist', icon: <CollectionsIcon /> },
          { id: 'collectionsSettings', label: 'Collections', icon: <CollectionsIcon /> },
          { id: 'billsOfCosts', label: 'Bills of Costs', icon: <BillingIcon /> },
        ]
      },
      {
        key: 'finance',
        label: 'Trust & Accounting',
        items: [
          { id: 'trustAccounting', label: 'Trust Accounting', icon: <TrustIcon /> },
          { id: 'chartOfAccounts', label: 'Chart of Accounts', icon: <ChartOfAccountsIcon /> },
          { id: 'generalLedger', label: 'General Ledger', icon: <GeneralLedgerIcon /> },
          { id: 'suppliers', label: 'Suppliers', icon: <SuppliersIcon /> },
          { id: 'creditors', label: 'Creditors', icon: <CreditorsIcon /> },
          { id: 'pettyCash', label: 'Petty Cash', icon: <PettyCashIcon /> },
          { id: 'accountingReview', label: 'Accounting Review', icon: <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> },
          { id: 'firmExpenses', label: 'Firm Expenses', icon: <FirmExpensesIcon /> },
          { id: 'bankAccounts', label: 'Bank Accounts', icon: <BankAccountsIcon /> },
          { id: 'bankReconciliation', label: 'Bank Reconciliation', icon: <BankReconIcon /> },
          { id: 'vatReturns', label: 'VAT Returns', icon: <VATReturnsIcon /> },
          { id: 'trialBalance', label: 'Trial Balance', icon: <ReportsIcon /> },
          { id: 'balanceSheet', label: 'Balance Sheet', icon: <ReportsIcon /> },
          { id: 'incomeStatement', label: 'Income Statement', icon: <ReportsIcon /> },
          { id: 'cashFlow', label: 'Cash Flow', icon: <ReportsIcon /> },
          { id: 'accountingPeriods', label: 'Accounting Periods', icon: <CalendarIcon /> },
          { id: 'reports', label: 'Reports', icon: <ReportsIcon /> },
        ]
      },
      {
        key: 'performance',
        label: 'People & Performance',
        items: [
          { id: 'activityLog', label: 'Activity Log', icon: <ActivityLogIcon /> },
          { id: 'myPerformance', label: 'My Performance', icon: <MyPerformanceIcon /> },
          { id: 'payrollManagement', label: 'Payroll & HR', icon: <TeamOverviewIcon /> },
          { id: 'teamOverview', label: 'Team Overview', icon: <TeamOverviewIcon /> },
          { id: 'performanceReviews', label: 'Reviews', icon: <ReviewsIcon /> },
          { id: 'departments', label: 'Departments', icon: <DepartmentsIcon /> },
        ]
      },
      {
        key: 'compliance',
        label: 'Compliance & Audit',
        items: [
          { id: 'complianceHub', label: 'Compliance Hub', icon: <ComplianceIcon /> },
          { id: 'attorneyRecords', label: 'Attorney Records', icon: <AttorneyRecordsIcon /> },
          { id: 'auditManagement', label: 'Audit Management', icon: <AuditIcon /> },
          { id: 'manageAuditors', label: 'Manage Auditors', icon: <AuditIcon /> },
        ]
      },
      {
        key: 'conveyancing',
        label: 'Conveyancing',
        items: [
          { id: 'conveyancingDashboard', label: 'Conv. Dashboard', icon: <Icon path="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /> },
          { id: 'properties', label: 'Property Register', icon: <Icon path="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /> },
          { id: 'bankInstructionInbox', label: 'Bank Inbox', icon: <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> },
          { id: 'conveyancingBatches', label: 'Batch Lodgements', icon: <Icon path="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /> },
          { id: 'conveyancingReports', label: 'Conv. Reports', icon: <Icon path="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /> },
        ]
      },
      {
        key: 'admin',
        label: 'Administration',
        items: [
          { id: 'imports', label: 'Imports', icon: <ImportsIcon /> },
          { id: 'firmSettings', label: 'Firm Settings', icon: <ComplianceIcon /> },
          { id: 'moduleAccess', label: 'Module Access', icon: <LockSidebarIcon /> },
          { id: 'integrations', label: 'Integrations', icon: <ServicesIcon /> },
          { id: 'security', label: 'Security (2FA)', icon: <LockSidebarIcon /> },
        ]
      },
    ], []);

    const filteredNavGroups = useMemo(() => {
      // filterItem used to hardcode 5 separate per-role arrays here,
      // independently of TAB_ACCESS in utils/rbac.js (which RoleGate
      // consults for content-area gating) - the two drifted out of sync
      // (e.g. ACCOUNTANT could see the "Firm Settings" link here but
      // RoleGate then denied the content). TAB_ACCESS is now the single
      // source of truth for both; see training-docs/_meta/permission-audit-findings.md.
      // The 4th arg (moduleAccess) also gates tabs a firm has turned off for
      // this junior role via Module Access Control — the backend hands the
      // resolved map to the frontend so nav / RoleGate / API stay in lockstep.
      const filterItem = (item) => canAccessTab(userRole, item.id, currentUser, moduleAccess);
      return navGroups
        .map(group => ({ ...group, items: group.items.filter(filterItem) }))
        .filter(group => group.items.length > 0);
    }, [userRole, navGroups, moduleAccess]);

    const navRef = useRef(null);

    const handleNavKeyDown = (e) => {
      const container = navRef.current;
      if (!container) return;
      const items = container.querySelectorAll('.sidebar-item');
      if (!items || items.length === 0) return;
      const activeIndex = Array.prototype.indexOf.call(items, document.activeElement);
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          const next = activeIndex < items.length - 1 ? activeIndex + 1 : 0;
          items[next].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          const prev = activeIndex > 0 ? activeIndex - 1 : items.length - 1;
          items[prev].focus();
          break;
        case 'Home':
          e.preventDefault();
          items[0].focus();
          break;
        case 'End':
          e.preventDefault();
          items[items.length - 1].focus();
          break;
        default:
          break;
      }
    };

    return (
      <aside id="main-sidebar" role="navigation" aria-label="Main navigation" className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <h1 className="text-2xl font-bold text-white">
            {isSidebarCollapsed ? 'LO' : 'LegalOps'}
          </h1>
        </div>
        <nav className="sidebar-nav" id="sidebar-nav-container">
          <div ref={navRef} onKeyDown={handleNavKeyDown}>
            {filteredNavGroups.map((group) => (
              <div key={group.key} className="sidebar-group">
                {group.label && !isSidebarCollapsed && (
                  <button
                    className="sidebar-group-header"
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={!collapsedGroups[group.key]}
                  >
                    <span>{group.label}</span>
                    <ChevronIcon isOpen={!collapsedGroups[group.key]} />
                  </button>
                )}
                {group.label && isSidebarCollapsed && (
                  <div className="sidebar-group-divider" />
                )}
                {(!group.label || !collapsedGroups[group.key] || isSidebarCollapsed) && group.items.map((item) => (
                  <div key={item.id}>
                    {isSidebarCollapsed ? (
                      <Tippy content={item.label} delay={[80, 0]}>
                        <NavLink
                          to={pathForTab(item.id)}
                          end={pathForTab(item.id) === '/'}
                          onClick={() => { setActiveTab(item.id); if (isMobileSidebarOpen) setIsMobileSidebarOpen(false); }}
                          className={({ isActive }) => `sidebar-item ${isActive || activeTab === item.id ? 'active' : ''}`}
                          title={item.label}
                          aria-label={item.label}
                        >
                          {React.cloneElement(item.icon, { className: "w-8 h-8" })}
                        </NavLink>
                      </Tippy>
                    ) : (
                      <NavLink
                        to={pathForTab(item.id)}
                        end={pathForTab(item.id) === '/'}
                        onClick={() => { setActiveTab(item.id); if (isMobileSidebarOpen) setIsMobileSidebarOpen(false); }}
                        className={({ isActive }) => `sidebar-item ${isActive || activeTab === item.id ? 'active' : ''}`}
                        aria-label={item.label}
                      >
                        {React.cloneElement(item.icon, { className: "w-6 h-6" })}
                        <span className="sidebar-label">{item.label}</span>
                      </NavLink>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </nav>
        <div className="sidebar-footer">
          {isSidebarCollapsed ? (
            <Tippy content="Logout" delay={[80, 0]}>
              <button onClick={handleLogout} className="sidebar-item" title="Logout" aria-label="Logout">
                <LogoutIcon />
              </button>
            </Tippy>
          ) : (
            <button onClick={handleLogout} className="sidebar-item" title="Logout">
              <LogoutIcon />
              <span className="sidebar-label">Logout</span>
            </button>
          )}
        </div>
      </aside>
    );
  };
  
  // --- Header Timer Indicator ---
  const HeaderTimerIndicator = () => {
    const { isTimerRunning, timerCaseTitle, elapsedTime, formatTime } = useTimer();
    if (!isTimerRunning) return null;
    return (
      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1 mr-3 animate-pulse-slow">
        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
        <span className="text-sm font-mono font-semibold text-green-700">{formatTime(elapsedTime)}</span>
        {timerCaseTitle && <span className="text-xs text-green-600 hidden md:inline max-w-[120px] truncate">{timerCaseTitle}</span>}
      </div>
    );
  };

  // --- Header Component ---
  const Header = () => (
    <header className="main-header">
       {/* --- NEW: Sidebar Toggle Button --- */}
      <button
        onClick={() => {
          // If on a small viewport, open mobile overlay instead of collapsing
          if (window.innerWidth < 768) {
            setIsMobileSidebarOpen(prev => !prev);
          } else {
            setIsSidebarCollapsed(prev => !prev);
          }
        }}
        className="p-2 rounded-full hover:bg-gray-200"
        aria-controls="main-sidebar"
        aria-expanded={isMobileSidebarOpen || !isSidebarCollapsed}
        aria-pressed={isSidebarCollapsed}
        aria-label={(isMobileSidebarOpen || !isSidebarCollapsed) ? 'Collapse navigation sidebar' : 'Open navigation sidebar'}
      >
          <MenuIcon isCollapsed={isSidebarCollapsed} />
      </button>
      <div className="flex-1">
        {/* Search bar can go here */}
      </div>
      <HeaderTimerIndicator />
      <div className="flex items-center space-x-4">
        {currentUser.is_superuser && firms.length > 0 && (
          <div className="relative">
             <select 
                value={selectedFirm || ''} 
                onChange={(e) => handleFirmSwitch(e.target.value)}
                className="firm-switcher"
              >
              <option value="">All Firms</option>
              {firms.map(firm => (
                <option key={firm.id} value={firm.id}>{firm.firm_name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="text-right">
          <p className="font-semibold text-gray-800">{currentUser.username}</p>
          <p className="text-sm text-gray-500">{userRole}</p>
        </div>
        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-xl">
          {currentUser.username.charAt(0).toUpperCase()}
        </div>
        {/* Notification bell */}
        <div className="relative">
          <button
            className="p-2 rounded-full hover:bg-gray-200"
            aria-label="Notifications"
            onClick={() => setShowNotificationsModal(true)}
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {notifications.filter(n => !n.is_read).length > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                {notifications.filter(n => !n.is_read).length}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );

  // --- Render Logic ---
  return (
    <>
      {/* Skip link for keyboard users */}
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <Routes>
        <Route path="/set-password/:uid/:token" element={<SetPassword />} />
        
        <Route path="*" element={
          <>
            {/* Rendered unconditionally so errors (e.g. a failed login) are visible
                before the user is authenticated, not just inside the app shell below. */}
            <Toast messages={toasts} dismissToast={dismissToast} />
            {authLoading ? (
            <div className="flex justify-center items-center h-screen bg-gray-100">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : !token || !currentUser ? (
            <AuthForms
              showLoginForm={showLoginForm}
              loginData={loginData}
              handleAuthInputChange={handleAuthInputChange}
              handleLoginSubmit={handleLoginSubmit}
              pending2FA={pending2FA}
              totpCode={totpCode}
              setTotpCode={setTotpCode}
              handle2FASubmit={handle2FASubmit}
              onCancel2FA={() => { setPending2FA(null); setTotpCode(''); }}
            />
          ) : (
            <PermissionsContext.Provider value={permissions}>
              <TimerProvider>
              {userRole === 'AUDITOR' ? (
                  <AuditorErrorBoundary handleLogout={handleLogout}>
                    <AuditorPortalLayout
                      currentUser={currentUser}
                      handleLogout={handleLogout}
                      authAxios={authAxios}
                      accountExpiresAt={currentUser?.account_expires_at}
                      showFormMessage={showFormMessage}
                    />
                  </AuditorErrorBoundary>
              ) : isStaff ? (
                  <div className="flex h-screen bg-gray-200 font-sans">
                    <QuickMemoWidget authAxios={authAxios} showFormMessage={showFormMessage} />
                    <Sidebar userRole={userRole} />
                    {isMobileSidebarOpen && (
                      <div className="sidebar-overlay" onClick={() => setIsMobileSidebarOpen(false)} aria-hidden="true"></div>
                    )}
                    <div className="flex-1 flex flex-col overflow-hidden">
                      <Header />
                      {showNotificationsModal && (
                        <NotificationList
                          notifications={notifications}
                          fetchNotifications={fetchNotifications}
                          authAxios={authAxios}
                          setShowNotificationsModal={setShowNotificationsModal}
                          navigateToItem={navigateToItem}
                        />
                      )}
                      <main id="main-content" className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-200 p-6" aria-hidden={isMobileSidebarOpen ? 'true' : 'false'}>
                        <Breadcrumbs />

                        {isCaseDetailsRoute ? (
                          <CaseDetailsPage
                            authAxios={authAxios}
                            caseId={caseRouteId}
                            initialTab={caseRouteSubtab}
                            onBack={onCaseDetailsBack}
                            showFormMessage={showFormMessage}
                            clients={clients}
                            setActiveTab={setActiveTab}
                            setNavigateTo={setNavigateTo}
                            moduleAccess={moduleAccess}
                          />
                        ) : (
                          <>
                        {activeTab === 'dashboard' && (
                          <div>
                            <div className="mb-6 px-6 pt-6">
                              <h2 className="text-3xl font-bold text-gray-800">Welcome back, {currentUser.username}!</h2>
                              <p className="text-gray-600">Here's a snapshot of your firm's activity.</p>
                            </div>
                            <Dashboard
                              currentUser={currentUser}
                              authAxios={authAxios}
                              showFormMessage={showFormMessage}
                              recentActivity={recentActivity}
                              handleActivityClick={handleActivityClick}
                              setActiveTab={setActiveTab}
                              setNavigateTo={setNavigateTo}
                            />
                          </div>
                        )}

                        {activeTab === 'clients' && (
                          <RoleGate tabId="clients" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ClientManagement
                              clients={clients} cases={cases} documents={documents}
                              timeEntries={timeEntries} expenseEntries={expenseEntries}
                              communicationLogs={communicationLogs} tasks={tasks} invoices={invoices}
                              invoiceItems={invoiceItems} serviceProducts={serviceProducts} users={users}
                              authAxios={authAxios} fetchClients={fetchClients} showFormMessage={showFormMessage}
                              handleDelete={handleDelete} currentUser={currentUser} genderOptions={genderOptions}
                              riskLevelOptions={riskLevelOptions} fetchComplianceItems={fetchComplianceItems}
                            />
                          </RoleGate>
                        )}
                        {/* ... (all other activeTab renderings remain the same) ... */}
                        {activeTab === 'cases' && (
                          <RoleGate tabId="cases" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <CaseManagement
                              cases={cases} clients={clients} users={users} authAxios={authAxios}
                              fetchCases={fetchCases} fetchTasks={fetchTasks} fetchInvoices={fetchInvoices} showFormMessage={showFormMessage}
                              handleDelete={handleDelete} currentUser={currentUser} caseTypeOptions={caseTypeOptions}
                              caseStatusOptions={caseStatusOptions} casePhaseOptions={casePhaseOptions}
                              caseStageOptions={caseStageOptions} caseTypeStagesMap={caseTypeStagesMap} getStagesForType={getStagesForType} navigateTo={navigateTo} setNavigateTo={setNavigateTo}
                              partners={partners} associatesList={associatesList} paralegalsList={paralegalsList}
                              setActiveTab={setActiveTab} navigateToItem={navigateToItem}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'documents' && !editingDocument && (
                          <RoleGate tabId="documents" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <DocumentManagement
                              documents={documents} cases={cases} users={users}
                              documentVersions={documentVersions} setDocumentVersions={setDocumentVersions}
                              documentTemplates={documentTemplates} authAxios={authAxios}
                              fetchDocuments={fetchDocuments} fetchDocumentVersions={fetchDocumentVersions}
                              fetchDocumentTemplates={fetchDocumentTemplates} showFormMessage={showFormMessage}
                              handleDelete={handleDelete} currentUser={currentUser}
                              documentTypeOptions={documentTypeOptions} clients={clients}
                              navigateTo={navigateTo} setNavigateTo={setNavigateTo}
                              onOpenEditor={(doc) => setEditingDocument(doc || { id: null, title: '', content: '' })}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'documents' && editingDocument && (
                          <RoleGate tabId="documents" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <DocumentEditorPage
                              authAxios={authAxios}
                              documentId={editingDocument.id}
                              initialContent={editingDocument.content || ''}
                              documentTitle={editingDocument.title || 'Untitled Document'}
                              onBack={() => { setEditingDocument(null); fetchDocuments(); }}
                              showFormMessage={showFormMessage}
                              currentUser={currentUser}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'clauseLibrary' && (
                          <RoleGate tabId="clauseLibrary" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ClauseLibrary
                              authAxios={authAxios}
                              showFormMessage={showFormMessage}
                              userRole={userRole}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'imports' && (
                          <RoleGate tabId="imports" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ImportWizard
                              authAxios={authAxios}
                              showFormMessage={showFormMessage}
                              documentTypeOptions={documentTypeOptions}
                              currentUser={currentUser}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'pendingApprovals' && (
                          <RoleGate tabId="pendingApprovals" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <PendingApprovalsInbox
                              authAxios={authAxios}
                              showFormMessage={showFormMessage}
                              setActiveTab={setActiveTab}
                              setNavigateTo={setNavigateTo}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'timeExpense' && (
                          <RoleGate tabId="timeExpense" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TimeExpenseManagement
                              cases={cases} clients={clients} timeEntries={timeEntries}
                              expenseEntries={expenseEntries} serviceProducts={serviceProducts}
                              authAxios={authAxios} fetchTimeEntries={fetchTimeEntries}
                              fetchExpenseEntries={fetchExpenseEntries} fetchInvoices={fetchInvoices}
                              showFormMessage={showFormMessage} handleDelete={handleDelete}
                              currentUser={currentUser} expenseCategoryOptions={expenseCategoryOptions}
                              navigateTo={navigateTo} setNavigateTo={setNavigateTo} userRole={userRole}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'billing' && (
                          <RoleGate tabId="billing" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <BillingManagement
                              clients={clients} cases={cases} userRole={userRole}
                              serviceProducts={serviceProducts} invoices={invoices}
                              invoiceItems={invoiceItems} authAxios={authAxios}
                              fetchInvoices={fetchInvoices} fetchInvoiceItems={fetchInvoiceItems}
                              showFormMessage={showFormMessage} handleDelete={handleDelete}
                              invoiceStatusOptions={invoiceStatusOptions} navigateTo={navigateTo}
                              setNavigateTo={setNavigateTo}
                            />
                          </RoleGate>
                        )}

                        {activeTab === 'debtCollectionDashboard' && (
                          <RoleGate tabId="debtCollectionDashboard" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <DebtCollectionDashboard
                              authAxios={authAxios}
                              showFormMessage={showFormMessage}
                              setActiveTab={setActiveTab}
                              setNavigateTo={setNavigateTo}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'collectionsWorklist' && (
                          <RoleGate tabId="collectionsWorklist" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <CollectionsWorklist
                              authAxios={authAxios}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'collectionsSettings' && (
                          <RoleGate tabId="collectionsSettings" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <CollectionsSettings
                              authAxios={authAxios}
                              permissions={permissions}
                            />
                          </RoleGate>
                        )}
                         {activeTab === 'trustAccounting' && (
                          <RoleGate tabId="trustAccounting" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TrustAccounting
                                authAxios={authAxios} clients={clients} cases={cases}
                                invoices={invoices} showFormMessage={showFormMessage}
                                fetchTrustAccounts={fetchTrustAccounts} fetchInvoices={fetchInvoices}
                                fetchPayments={fetchPayments}
                            navigateTo={navigateTo}
                            setNavigateTo={setNavigateTo}
                            navigateToAuditSupportTrustSummary={navigateToAuditSupportTrustSummary}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'communicationLogs' && (
                          <RoleGate tabId="communicationLogs" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <CommunicationLogManagement
                              communicationLogs={communicationLogs} clients={clients} cases={cases}
                              users={users} authAxios={authAxios} fetchCommunicationLogs={fetchCommunicationLogs}
                              showFormMessage={showFormMessage} handleDelete={handleDelete}
                              currentUser={currentUser} communicationLogTypeOptions={communicationLogTypeOptions}
                              navigateTo={navigateTo} setNavigateTo={setNavigateTo} setActiveTab={setActiveTab}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'tasks' && (
                          <RoleGate tabId="tasks" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TaskManagement
                              tasks={tasks} cases={cases} users={users}
                              paralegalsList={paralegalsList} userRole={userRole}
                              authAxios={authAxios}
                              fetchTasks={fetchTasks} showFormMessage={showFormMessage}
                              handleDelete={handleDelete} currentUser={currentUser}
                              taskStatusOptions={taskStatusOptions} taskPriorityOptions={taskPriorityOptions}
                              navigateTo={navigateTo} setNavigateTo={setNavigateTo}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'taskTemplates' && (
                          <RoleGate tabId="taskTemplates" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TaskTemplatesManager
                              authAxios={authAxios}
                              permissions={permissions}
                              showFormMessage={showFormMessage}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'serviceProducts' && (
                          <RoleGate tabId="serviceProducts" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ServiceProductManagement
                              serviceProducts={serviceProducts} authAxios={authAxios}
                              fetchServiceProducts={fetchServiceProducts} showFormMessage={showFormMessage}
                              handleDelete={handleDelete} userRole={userRole}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'reports' && (
                          <RoleGate tabId="reports" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ReportsDashboard
                              authAxios={authAxios}
                              financialReports={financialReports}
                              caseloadReports={caseloadReports}
                              performanceMetrics={performanceMetrics}
                              insightsReports={insightsReports}
                              userRole={userRole}
                              reportDateRange={reportDateRange}
                              setReportDateRange={setReportDateRange}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'calendar' && (
                          <CalendarModule
                            authAxios={authAxios} showFormMessage={showFormMessage}
                            navigateToItem={navigateToItem} userRole={userRole}
                            currentUser={currentUser} cases={cases}
                          />
                        )}
                        {activeTab === 'auditManagement' && (
                          <RoleGate tabId="auditManagement" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <AuditManagement
                              authAxios={authAxios} users={users} clients={clients}
                              showFormMessage={showFormMessage} userRole={userRole}
                              navigateToItem={navigateToItem}
                              navigateTo={navigateTo}
                              setNavigateTo={setNavigateTo}
                              navigateToTrustLedger={navigateToTrustLedger}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'complianceHub' && (
                          <RoleGate tabId="complianceHub" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ComplianceHub
                              authAxios={authAxios} users={users} clients={clients}
                              showFormMessage={showFormMessage} fetchComplianceItems={fetchComplianceItems}
                              fetchClients={fetchClients} userRole={userRole}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'attorneyRecords' && (
                          <RoleGate tabId="attorneyRecords" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <AttorneyRecords
                              authAxios={authAxios}
                              users={users}
                              userRole={userRole}
                              showFormMessage={showFormMessage}
                              navigateTo={navigateTo}
                              setNavigateTo={setNavigateTo}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'annualCertificateManagement' && (
                          <RoleGate tabId="annualCertificateManagement" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <AnnualCertificateManagement
                              authAxios={authAxios} users={users || []}
                              showFormMessage={showFormMessage} userRole={userRole}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'manageAuditors' && (
                          <RoleGate tabId="manageAuditors" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <AuditorAccountManager
                              authAxios={authAxios}
                              showFormMessage={showFormMessage}
                              firms={firms}
                            />
                          </RoleGate>
                        )}
                        {activeTab === 'chartOfAccounts' && (
                          <RoleGate tabId="chartOfAccounts" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ChartOfAccounts authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'generalLedger' && (
                          <RoleGate tabId="generalLedger" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <GeneralLedger authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'suppliers' && (
                          <RoleGate tabId="suppliers" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <SupplierManagement authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'creditors' && (
                          <RoleGate tabId="creditors" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <CreditorManagement authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'pettyCash' && (
                          <RoleGate tabId="pettyCash" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <PettyCashManagement authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'accountingReview' && (
                          <RoleGate tabId="accountingReview" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <AccountingReview authAxios={authAxios} showFormMessage={showFormMessage} setActiveTab={setActiveTab} setNavigateTo={setNavigateTo} />
                          </RoleGate>
                        )}
                        {activeTab === 'firmExpenses' && (
                          <RoleGate tabId="firmExpenses" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <FirmExpenseManagement authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'bankAccounts' && (
                          <RoleGate tabId="bankAccounts" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <BankAccountSettings authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'bankReconciliation' && (
                          <RoleGate tabId="bankReconciliation" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <BankReconciliation authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'vatReturns' && (
                          <RoleGate tabId="vatReturns" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <VATReturns authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'trialBalance' && (
                          <RoleGate tabId="trialBalance" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TrialBalanceReport authAxios={authAxios} />
                          </RoleGate>
                        )}
                        {activeTab === 'balanceSheet' && (
                          <RoleGate tabId="balanceSheet" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <BalanceSheetReport authAxios={authAxios} />
                          </RoleGate>
                        )}
                        {activeTab === 'incomeStatement' && (
                          <RoleGate tabId="incomeStatement" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <IncomeStatementReport authAxios={authAxios} />
                          </RoleGate>
                        )}
                        {activeTab === 'cashFlow' && (
                          <RoleGate tabId="cashFlow" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <CashFlowReport authAxios={authAxios} />
                          </RoleGate>
                        )}
                        {activeTab === 'accountingPeriods' && (
                          <RoleGate tabId="accountingPeriods" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <AccountingPeriods authAxios={authAxios} />
                          </RoleGate>
                        )}
                        {activeTab === 'billsOfCosts' && (
                          <RoleGate tabId="billsOfCosts" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <BillOfCostsManagement authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'firmSettings' && (
                          <RoleGate tabId="firmSettings" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <FirmTaxSettings authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'moduleAccess' && (
                          <RoleGate tabId="moduleAccess" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ModuleAccessControls authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'integrations' && (
                          <RoleGate tabId="integrations" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <IntegrationsSettings authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} />
                          </RoleGate>
                        )}
                        {activeTab === 'security' && (
                          <RoleGate tabId="security" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TwoFactorSetup authAxios={authAxios} showFormMessage={showFormMessage} />
                          </RoleGate>
                        )}
                        {activeTab === 'unlinkedInbox' && (
                          <RoleGate tabId="unlinkedInbox" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <UnlinkedInbox authAxios={authAxios} showFormMessage={showFormMessage} cases={cases} />
                          </RoleGate>
                        )}
                        {activeTab === 'emailRules' && (
                          <RoleGate tabId="emailRules" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <EmailRules authAxios={authAxios} showFormMessage={showFormMessage} cases={cases} />
                          </RoleGate>
                        )}
                        {activeTab === 'activityLog' && (
                          <RoleGate tabId="activityLog" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ActivityLog authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} currentUser={currentUser} />
                          </RoleGate>
                        )}
                        {activeTab === 'myPerformance' && (
                          <RoleGate tabId="myPerformance" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <MyPerformance authAxios={authAxios} showFormMessage={showFormMessage} currentUser={currentUser} />
                          </RoleGate>
                        )}
                        {activeTab === 'teamOverview' && (
                          <RoleGate tabId="teamOverview" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <TeamOverview authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} currentUser={currentUser} />
                          </RoleGate>
                        )}
                        {activeTab === 'payrollManagement' && (
                          <RoleGate tabId="payrollManagement" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <PayrollManagement authAxios={authAxios} addToast={(text, type) => showFormMessage(type, text)} />
                          </RoleGate>
                        )}
                        {activeTab === 'performanceReviews' && (
                          <RoleGate tabId="performanceReviews" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <PerformanceReviews authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} currentUser={currentUser} users={users} />
                          </RoleGate>
                        )}
                        {activeTab === 'departments' && (
                          <RoleGate tabId="departments" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <DepartmentManagement authAxios={authAxios} showFormMessage={showFormMessage} userRole={userRole} currentUser={currentUser} users={users} />
                          </RoleGate>
                        )}
                        {activeTab === 'conveyancingDashboard' && (
                          <RoleGate tabId="conveyancingDashboard" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ConveyancingDashboard authAxios={authAxios} showFormMessage={showFormMessage} setActiveTab={setActiveTab} />
                          </RoleGate>
                        )}
                        {activeTab === 'properties' && (
                          <RoleGate tabId="properties" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <PropertyManagement authAxios={authAxios} showFormMessage={showFormMessage} />
                          </RoleGate>
                        )}
                        {activeTab === 'bankInstructionInbox' && (
                          <RoleGate tabId="bankInstructionInbox" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <BankInstructionInbox authAxios={authAxios} showFormMessage={showFormMessage} />
                          </RoleGate>
                        )}
                        {activeTab === 'conveyancingBatches' && (
                          <RoleGate tabId="conveyancingBatches" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ConveyancingBatchManager authAxios={authAxios} showFormMessage={showFormMessage} />
                          </RoleGate>
                        )}
                        {activeTab === 'conveyancingReports' && (
                          <RoleGate tabId="conveyancingReports" userRole={userRole} currentUser={currentUser} moduleAccess={moduleAccess}>
                            <ConveyancingReports authAxios={authAxios} />
                          </RoleGate>
                        )}
                          </>
                        )}
                      </main>
                    </div>
                  </div>
              ) : (
                <ClientPortalLayout
                  currentUser={currentUser}
                  handleLogout={handleLogout}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                  clients={clients}
                  cases={cases}
                  documents={documents}
                  invoices={invoices}
                  invoiceItems={invoiceItems}
                  communicationLogs={communicationLogs}
                  tasks={tasks}
                  timeEntries={timeEntries}
                  expenseEntries={expenseEntries}
                  authAxios={authAxios}
                  showFormMessage={showFormMessage}
                  debtRecoveryMandates={debtRecoveryMandates}
                />
              )}
            </TimerProvider>
            </PermissionsContext.Provider>
          )}
          </>
        }/>
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
